import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { AsyncPipe, NgClass, NgFor, NgForOf, NgIf } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCartPlus, faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import { ProductService } from '../../service/product-service';
import { ActivatedRoute, Router } from '@angular/router';
import { Product as ProductModel } from '../../models/product';
import { firstValueFrom, Observable, of } from 'rxjs';
import { Color } from '../../models/color';
import { Size } from '../../models/size';
import { CartService } from '../../service/cart-service';
import { GiftBoxService } from '../../service/gift-box-service';
import { SelectedProduct } from '../../models/selected-product';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ItemsLoading } from '../../components/items-loading/items-loading';
import { OrderService } from '../../service/order-service';

@Component({
  selector: 'app-single-product',
  imports: [FormsModule, NgFor, FontAwesomeModule, NgIf, NgForOf, NgClass, ItemsLoading],

  templateUrl: './single-product.html',
  styleUrl: './single-product.scss'
})
export class SingleProduct implements OnInit {
  faCartPlus = faCartPlus;
  faBoxOpen = faBoxOpen;
  private productService = inject(ProductService);
  private activatedRoute = inject(ActivatedRoute);
  private cartService = inject(CartService);
  private giftBoxService = inject(GiftBoxService);
  private router = inject(Router);
  productImages: string[] = [];
  selectedImage: string = '';
  product: ProductModel | undefined;
  quantity = 1;
  selectedColor: Color | undefined;
  selectedSize: Size | undefined;
  private snackBar = inject(MatSnackBar);
  protected addToCartLoading: boolean = false;
  private orderService = inject(OrderService);
  protected price: number;
  protected oldPrice: number;
  protected avaialbleQuantity: number;

  addToGiftBox() {
    if (!this.product) return;
    this.giftBoxService.addItem(this.product, this.quantity);
    this.snackBar.open(`🎁 Added ${this.quantity}x "${this.product.name}" to your Gift Box!`, 'View Box', {
      duration: 3500,
    }).onAction().subscribe(() => {
      this.router.navigate(['/gifts']);
    });
  }


  constructor() {
    this.price = this.product?.price!;
    this.oldPrice = this.product?.oldPrice!;
    this.avaialbleQuantity = this.product?.quantity!;
  }


  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      const productId = params['id'];
      // Fetch product details using the productId
      this.productService.getProduct(productId).subscribe(product => {
        if (product) {
          this.product = product
          this.selectedColor = this.product.colors[0]
          this.selectedSize = this.product.sizes[0]
          
          const allImages = [...(this.product.images || []) as string[]];
          if (this.product.availableCombinations) {
            this.product.availableCombinations.forEach(c => {
               if (c.images) {
                 c.images.forEach(img => {
                   if (!allImages.includes(img)) {
                     allImages.push(img);
                   }
                 });
               }
            });
          }
          this.productImages = allImages;

          this.updateImagesByVariant();

          this.activatedRoute.queryParams.subscribe(query => {
            const colorParam = query['color'];
            const sizeParam = query['size'];
            const qtyParam = query['quantity'];

            if (colorParam) {
              const matchColor = this.product!.colors.find(
                c => c.name.toLowerCase() === colorParam.toLowerCase()
              );
              if (matchColor) this.selectedColor = matchColor;
            }

            if (sizeParam) {
              const matchSize = this.product!.sizes.find(
                s => s.toLowerCase() === sizeParam.toLowerCase()
              );
              if (matchSize) this.selectedSize = matchSize;
            }

            if (qtyParam && !isNaN(qtyParam)) {
              this.quantity = Number(qtyParam);
            }
          })
        }
      })
    });
  }

  updateImagesByVariant() {
    if (!this.product) return;

    const match = this.product.availableCombinations?.find(c =>
      (!c.color || c.color.code === this.selectedColor?.code) &&
      (!c.size || c.size === this.selectedSize)
    );
    if (match && match.images?.length) {
      this.price = match.price;
      this.oldPrice = match.oldPrice;
      this.avaialbleQuantity = match.quantity;
      this.selectedImage = match.images[0];
    } else {
      // fallback to default product images
      this.selectedImage = (this.product.images as string[])[0];
      this.price = this.product.price;
      // this.avaialbleQuantity = this.product.quantity
      this.oldPrice = this.product.oldPrice;
    }
  }

  onImageError(event: Event) {
    const element = event.target as HTMLImageElement;
    element.src = '/placeholder.svg';
  }

  onColorChange(color: Color) {
    this.selectedColor = color;
    this.updateImagesByVariant();
  }

  selectImage(img: string) {
    this.selectedImage = img;
    if (!this.product || !this.product.availableCombinations) return;

    const matchingCombo = this.product.availableCombinations.find(c =>
      c.images && c.images.includes(img)
    );

    if (matchingCombo) {
      if (matchingCombo.color) {
        const colorMatch = this.product.colors?.find(c => c.code === matchingCombo.color?.code);
        if (colorMatch) this.selectedColor = colorMatch;
      }
      if (matchingCombo.size) {
        this.selectedSize = matchingCombo.size;
      }
      this.price = matchingCombo.price;
      this.oldPrice = matchingCombo.oldPrice;
      this.avaialbleQuantity = matchingCombo.quantity;
    }
  }

  onSizeChange(size: Size) {
    this.selectedSize = size;
    this.updateImagesByVariant();
  }

  async addToCart(product: ProductModel, qty: number) {
    this.addToCartLoading = true;
    const selectedProduct: SelectedProduct = {
      id: product.id!,
      product: product,
      size: this.selectedSize!,
      color: this.selectedColor!,
      price: this.price,
      oldPrice: this.oldPrice,
      images: [this.selectedImage, ...this.productImages.filter(i => i !== this.selectedImage)],
      quantity: this.quantity
    }

    const available = await firstValueFrom(this.orderService.checkProductAvailability(selectedProduct));
    if (!available.available) {
      this.snackBar.open("Sorry, this product is currently out of stock.", 'Close', { duration: 5000 });
      this.addToCartLoading = false;
      return;
    }
    // this.cartService.addToCart(selectedProduct).subscribe(res => {
    //   console.log('Add to cart response:', res);
    // },(error)=>{

    // })
    this.cartService.addToCart(selectedProduct).subscribe({
      next: (res) => {
        this.snackBar.open("Item added to cart successfully!", 'Close', { duration: 5000 });
        this.addToCartLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.snackBar.open("Failed to add item to cart!", 'Close', { duration: 5000 });
        this.addToCartLoading = false;
        if (error?.message === 'User not logged in') {
          this.router.navigate(['/sign-in']);
        }
      }
    });

  }

}
